import React from 'react';

import { Quote } from '@client/model';

import { formatCurrency } from '@shared/formatCurrency';
import { CARRIER_NAMES } from '@shared';

function formatRiskClass(
  carrierName: string,
  primaryTobaccoStatus: string,
  secondaryTobaccoStatus?: string,
): string {
  const isNationwide = carrierName === CARRIER_NAMES.NATIONWIDE;

  if (!secondaryTobaccoStatus) {
    return isNationwide ? `Standard ${primaryTobaccoStatus}` : primaryTobaccoStatus;
  }

  // Joint Life policy (has secondaryTobaccoStatus)
  const formatJointStatus = (status: string) => (isNationwide ? `Preferred ${status}` : status);
  const primary = formatJointStatus(primaryTobaccoStatus);
  const secondary = formatJointStatus(secondaryTobaccoStatus);
  return `${primary} + ${secondary}`;
}

type ProductDetailEntry = {
  label: string;
  value: string | number;
  testId?: string;
};

type QuoteDetailSectionProps = {
  title: string;
  items: ProductDetailEntry[];
};

function QuoteDetailSection({ title, items }: QuoteDetailSectionProps) {
  return (
    <div className="select-none">
      <h4 className="text-primary-dark mb-2 text-sm font-bold">{title}</h4>
      <div className="space-y-1">
        {items.map((item, index) => (
          <p
            key={index}
            className="text-primary-dark/90 text-sm leading-relaxed"
            data-testid={item.testId}
          >
            {item.label && <span className="font-medium">{item.label}: </span>}
            {item.value}
          </p>
        ))}
      </div>
    </div>
  );
}

export function ProductDetails({ quote }: { quote: Quote }) {
  const {
    aobDuration,
    cobDuration,
    ltcBenefitMonthlyIndividual,
    ltcBenefitAnnualIndividual,
    ltcBenefitTotal,
    ltcBenefitDurationMonths,
    deathBenefitMinimumGuaranteedIndividual,
    premiumAmount,
    aobInflation,
    cobInflation,
    inflationBenefitOption,
    inflationDuration,
    notes,
    carrierName,
    ltcPremium,
    lifePremium,
    returnOfPremiumPercent,
    maxMonthlyLTCBenefitReimbursement,
    maxMonthlyLTCBenefitIndemnity,
    hasMec,
  } = quote;

  const getLtcBenefitPeriod = () => {
    if (!ltcBenefitDurationMonths) return null;

    const isLincoln = carrierName === CARRIER_NAMES.LINCOLN_FINANCIAL;
    const isNationwide = carrierName === CARRIER_NAMES.NATIONWIDE;
    const isSecurian = carrierName === CARRIER_NAMES.SECURIAN_FINANCIAL;

    if (isLincoln || isNationwide || isSecurian) {
      if (quote.policyType === 'Joint Life') {
        return `${ltcBenefitDurationMonths} months`;
      } else {
        const years = ltcBenefitDurationMonths / 12;
        return `${years} year${years === 1 ? '' : 's'}`;
      }
    }
    return null;
  };

  const isLincolnWithMaxBenefits = carrierName === CARRIER_NAMES.LINCOLN_FINANCIAL &&
    (maxMonthlyLTCBenefitReimbursement || maxMonthlyLTCBenefitIndemnity);

  const benefitLimitItems: ProductDetailEntry[] = [
    // Show LTC Coverage for non-Lincoln carriers
    ...(!isLincolnWithMaxBenefits
      ? [
          {
            label: 'LTC Coverage',
            value: `${formatCurrency(Math.ceil(quote.ltcBenefitMonthlyIndividual))}/month ${quote.policyType === 'Joint Life' ? 'per insured' : ''}`,
          },
        ]
      : []),
    ...(isLincolnWithMaxBenefits && maxMonthlyLTCBenefitReimbursement
      ? [
          {
            label: 'Max Monthly (Reimbursement)',
            value: `${formatCurrency(Math.ceil(maxMonthlyLTCBenefitReimbursement))}/month`,
          },
        ]
      : []),
    // Lincoln-specific: Show max indemnity
    ...(isLincolnWithMaxBenefits && maxMonthlyLTCBenefitIndemnity
      ? [
          {
            label: 'Max Monthly (Indemnity)',
            value: `${formatCurrency(Math.ceil(maxMonthlyLTCBenefitIndemnity))}/month`,
          },
        ]
      : []),
    ...(ltcBenefitAnnualIndividual
      ? [
          {
            label: 'Annual',
            value: `${formatCurrency(Math.ceil(ltcBenefitAnnualIndividual))} ${quote.policyType === 'Joint Life' ? 'per insured' : ''}`,
          },
        ]
      : []),
    ...[
      {
        label: 'Total LTC Benefit',
        value: `${ltcBenefitTotal === 0 ? 'Unlimited' : formatCurrency(Math.ceil(ltcBenefitTotal))}`,
      },
    ],
    ...(getLtcBenefitPeriod()
      ? [{ label: 'LTC Benefit Period', value: getLtcBenefitPeriod()!, testId: 'quote-detail-benefit-duration' }]
      : []),
      ...(deathBenefitMinimumGuaranteedIndividual
      ? [
          {
            label: 'Min Guaranteed Death Benefit',
            value: formatCurrency(Math.ceil(deathBenefitMinimumGuaranteedIndividual)),
          },
        ]
      : []),
    {
      label: 'Death Benefit',
      value: formatCurrency(Math.ceil(quote.deathBenefitInitial)),
    },
  ];

  const is10PayQuote = quote.cobPaymentOption === '10-Pay';
  const isLincoln = carrierName === CARRIER_NAMES.LINCOLN_FINANCIAL;

  const premiumItems: ProductDetailEntry[] = [
    {
      label: is10PayQuote ? 'Annual Planned Premium' : 'Total Premium',
      value: formatCurrency(Math.ceil(premiumAmount)),
      testId: 'quote-detail-premium',
    },
    // Lincoln-specific: Return of Premium
    ...(isLincoln && returnOfPremiumPercent
      ? [
          {
            label: 'Return of Premium',
            value: `${returnOfPremiumPercent}%`,
          },
        ]
      : []),
  ];

  const getInflationOptionText = () => {
    const parts = [];
    if (aobInflation) parts.push(`${aobInflation} AOB`);
    if (cobInflation) parts.push(`${cobInflation} COB`);
    if (inflationBenefitOption) parts.push(inflationBenefitOption);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const featureItems: ProductDetailEntry[] = [
    ...(aobDuration && cobDuration
      ? [
          {
            label: 'Benefit Period',
            value: `${aobDuration} AOB/${cobDuration} COB`,
            testId: 'quote-detail-benefit-duration',
          },
        ]
      : []),
    {
      label: 'Inflation Option',
      value: getInflationOptionText(),
      testId: 'quote-detail-inflation-protection',
    },
    ...(inflationDuration ? [{ label: 'Inflation Duration', value: inflationDuration }] : []),
    ...(carrierName === CARRIER_NAMES.LINCOLN_FINANCIAL
      ? [
          {
            label: 'Policy Type',
            value: quote.policyType,
          },
        ]
      : [
          {
            label: 'Class',
            value: formatRiskClass(carrierName, quote.primaryTobaccoStatus, quote.secondaryTobaccoStatus),
          },
        ]),
    ...(hasMec ? [{ label: 'MEC', value: 'Yes' }] : []),
    ...(notes ? [{ label: '', value: notes }] : []),
  ];

  const totalItems = benefitLimitItems.length + premiumItems.length + featureItems.length;
  // Use 3 columns when we have 8+ items for better distribution and visual balance
  const useThreeColumns = totalItems >= 8;
  const columnClass = useThreeColumns ? 'md:grid-cols-3' : 'md:grid-cols-2';

  // For 3-column layout, place the smallest section in the middle for better balance
  const sections = useThreeColumns
    ? [
        { title: 'Initial LTC Benefit Limit', items: benefitLimitItems },
        { title: 'Features and Benefits', items: featureItems },
        { title: 'Premium', items: premiumItems },
      ]
    : [
        { title: 'Initial LTC Benefit Limit', items: benefitLimitItems },
        { title: 'Premium', items: premiumItems },
        { title: 'Features and Benefits', items: featureItems },
      ];

  return (
    <div className="mt-6">
      <div className={`grid grid-cols-1 gap-6 ${columnClass}`}>
        {sections.map((section, index) => (
          <QuoteDetailSection
            key={index}
            title={section.title}
            items={section.items}
          />
        ))}
      </div>
    </div>
  );
}
